import { ApiService, ApiSyncStatus } from "@incendium/api";
import {
  Box,
  Button,
  Divider,
  List,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { syncService } from "Apis";
import { StyledDrawerContainer } from "Components/UI/StyledDrawer";
import ConnectButton from "features/dataConnectors/components/ConnectButton";
import SyncStyledListItem from "features/dataConnectors/components/SyncStyledListItem";
import WooCommerceConnectButton from "features/dataConnectors/components/WooCommerceConnectButton";
import WooCommerceConnectionStatus from "features/dataConnectors/components/WooCommerceConnectionStatus";
import WooCommercePluginSettings from "features/dataConnectors/components/WooCommercePluginSettings";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import { ISyncConfigComponentProps } from "features/dataConnectors/types";
import { getWooCommerceConnectionStatus } from "features/dataConnectors/utils/woocommerceStoreMeta";
import { useNotification, useSelectedProject } from "Hooks";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useSyncClients } from "Hooks/useSyncClients";
import produce from "immer";
import { Fragment, useCallback, useState } from "react";
import { useConfirmationContext } from "Providers/ConfirmationProvider";

function WooCommerce({ syncs }: ISyncConfigComponentProps) {
  const { selectedProject } = useSelectedProject();
  const { syncClients, setSyncClients } = useSyncClients();
  const {
    externalSyncs,
    setExternalSyncs,
    refetch: refetchExternalSyncs,
    loading: externalSyncsLoading,
  } = useExternalSyncs();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  const oauthSyncRedirect = OAUTH_REDIRECT_URL;
  const hasStore = (syncs || []).length > 0;

  const refreshConnectionStatus = useCallback(async () => {
    setRefreshingStatus(true);
    try {
      await refetchExternalSyncs();
    } catch {
      showErrorNotification("Failed to refresh connection status.");
    } finally {
      setRefreshingStatus(false);
    }
  }, [refetchExternalSyncs, showErrorNotification]);

  const removeStore = useCallback(
    async (id: number) => {
      openConfirmation({
        title: (
          <>
            <Typography variant="subtitle1">
              Are you sure you want to remove this WooCommerce store?
            </Typography>
            <Typography variant="body2">
              Doing so will remove all data associated with this store.
            </Typography>
          </>
        ),
        callback: async () => {
          try {
            await syncService.syncServiceDeleteSync({
              projectId: selectedProject?.id as number,
              syncId: id,
            });

            setExternalSyncs(
              produce(externalSyncs, (draft) => {
                const idx = draft.findIndex((d) => d.id === id);
                if (idx >= 0) {
                  draft.splice(idx, 1);
                }
              })
            );

            showSuccessNotification("Store removed.");
          } catch {
            showErrorNotification("Failed to remove store.");
          }

          closeConfirmation();
        },
      });
    },
    [
      openConfirmation,
      closeConfirmation,
      selectedProject,
      showSuccessNotification,
      showErrorNotification,
      externalSyncs,
      setExternalSyncs,
    ]
  );

  const onConnected = useCallback(async () => {
    try {
      await refetchExternalSyncs();
      showSuccessNotification("Store connected successfully.");
    } catch {
      showErrorNotification("Failed to refresh store connection status.");
    }
  }, [refetchExternalSyncs, showSuccessNotification, showErrorNotification]);

  return (
    <Box sx={{ maxWidth: 600 }}>
      <StyledDrawerContainer>
        <Box>
          <Typography variant="subtitle1">WooCommerce Store</Typography>
          <Typography variant="body2" mb={2}>
            {hasStore
              ? "Your connected store for this project"
              : "Connect your WooCommerce store for accurate tracking and ecommerce insights."}
          </Typography>

          {hasStore ? (
            <List>
              {(syncs || []).map((s) => {
                const showPluginSettings =
                  getWooCommerceConnectionStatus(s).phase !== "oauth_error";
                return (
                <Fragment key={s.id}>
                  <SyncStyledListItem
                    disableGutters
                    error={s.status === ApiSyncStatus.ERROR}
                    secondaryAction={
                      <Stack spacing={1}>
                        {s.status === ApiSyncStatus.ERROR && (
                          <ConnectButton
                            service={ApiService.WOOCOMMERCE}
                            syncClients={syncClients}
                            setSyncClients={setSyncClients}
                            oauthClientId={s.oauthClientId}
                            onConnected={onConnected}
                            state={oauthSyncRedirect}
                            shop={s.account ? `https://${s.account}` : undefined}
                            linkAccountProjectId={selectedProject?.id as number}
                          />
                        )}
                        <Button
                          size="extraSmall"
                          variant="outlined"
                          onClick={() => removeStore(s.id as number)}
                        >
                          Remove Store
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={s.accountName}
                      secondary={
                        s.status === ApiSyncStatus.ERROR ? (
                          <Typography variant="subtitle2" color="error">
                            This connection is no longer valid, please reconnect
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {s.client?.user?.name}
                          </Typography>
                        )
                      }
                    />
                  </SyncStyledListItem>
                  <Divider sx={{ my: 2 }} />
                  <WooCommerceConnectionStatus
                    sync={s}
                    onRefresh={refreshConnectionStatus}
                    refreshing={refreshingStatus || externalSyncsLoading}
                  />
                  {showPluginSettings && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <WooCommercePluginSettings
                        onSaved={refreshConnectionStatus}
                      />
                    </>
                  )}
                </Fragment>
              );
              })}
            </List>
          ) : (
            <WooCommerceConnectButton
              service={ApiService.WOOCOMMERCE}
              syncClients={syncClients}
              setSyncClients={setSyncClients}
              state={oauthSyncRedirect}
              linkAccountProjectId={selectedProject?.id as number}
              onConnected={onConnected}
            />
          )}
        </Box>
      </StyledDrawerContainer>
    </Box>
  );
}

export default WooCommerce;
